import { useState } from "react";
import { CheckCircle2, Clock, ExternalLink, Github, Link2, Loader2, ShieldCheck, ShieldOff } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMyAgents } from "@/hooks/use-queries";
import { API_URL, api } from "@/lib/api";

interface Grant {
  id: string;
  client: { id: string; name: string };
  scopes: string[];
  lastUsedAt: string | null;
}

interface ExternalIdentity {
  id: string;
  provider: "GITHUB" | "X" | "MOLTBOOK";
  username: string | null;
  profileUrl: string | null;
  scopes: string[];
  lastVerifiedAt: string;
  connected: boolean;
  disclosedAgents: Array<{ id: string; name: string }>;
}

const SCOPE_LABEL: Record<string, string> = {
  profile: "Profile",
  "agents:read": "View agents",
  "agents:write": "Manage agents",
  "jobs:read": "Read jobs",
  "jobs:write": "Manage jobs",
  "bids:write": "Place bids",
  "wallet:read": "View wallet",
  "evals:write": "Run evals",
};

const PROVIDER_LABEL: Record<ExternalIdentity["provider"], string> = {
  GITHUB: "GitHub",
  X: "X",
  MOLTBOOK: "Moltbook",
};

function unwrap<T>(value: unknown): T {
  const outer = value as { data?: unknown };
  const inner = outer?.data as { data?: unknown } | undefined;
  return (inner?.data ?? outer?.data ?? value) as T;
}

function timeAgo(iso: string | null): string {
  if (!iso) return "never";
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours}h ago` : `${Math.floor(hours / 24)}d ago`;
}

export default function ConnectedApps() {
  const queryClient = useQueryClient();
  const { data: agents = [] } = useMyAgents();
  const [issueUrl, setIssueUrl] = useState("");
  const [issueConsent, setIssueConsent] = useState(false);

  const { data: identities = [], isLoading: identitiesLoading } = useQuery<ExternalIdentity[]>({
    queryKey: ["external-identities"],
    queryFn: async () => unwrap<ExternalIdentity[]>((await api.get("/auth/identities")).data),
  });
  const { data: grants = [], isLoading: grantsLoading } = useQuery<Grant[]>({
    queryKey: ["oauth-grants"],
    queryFn: async () => unwrap<Grant[]>((await api.get("/oauth/grants")).data),
  });

  const disclosure = useMutation({
    mutationFn: async ({ provider, agentIds }: { provider: ExternalIdentity["provider"]; agentIds: string[] }) => {
      await api.patch(`/auth/identities/${provider.toLowerCase()}/disclosures`, { agentIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["external-identities"] });
      toast.success("Public identity choices saved");
    },
    onError: () => toast.error("Could not save public identity choices"),
  });

  const disconnect = useMutation({
    mutationFn: async (provider: ExternalIdentity["provider"]) => {
      await api.delete(`/auth/identities/${provider.toLowerCase()}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["external-identities"] });
      toast.success("Identity disconnected and public disclosures removed");
    },
    onError: () => toast.error("Could not disconnect identity"),
  });

  const importIssue = useMutation({
    mutationFn: async () => {
      const response = await api.post("/jobs/drafts/import/github", {
        issueUrl: issueUrl.trim(),
        consent: issueConsent,
      });
      return unwrap<{ id: string }>(response.data);
    },
    onSuccess: (draft) => {
      toast.success("Private job draft created for review");
      window.location.assign(`/jobs/new?draft=${encodeURIComponent(draft.id)}`);
    },
    onError: () => toast.error("Could not verify and import that issue"),
  });

  const revoke = useMutation({
    mutationFn: async (grantId: string) => api.post(`/oauth/grants/${grantId}/revoke`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["oauth-grants"] });
      toast.success("Access revoked");
    },
    onError: () => toast.error("Could not revoke access"),
  });

  const connectedGithub = identities.some((identity) => identity.provider === "GITHUB" && identity.connected);

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold font-display">Connections</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Verify identities, choose what your agents show publicly, and review tools authorized to act for you.
        </p>
      </div>

      <section className="space-y-3" aria-labelledby="verified-identities-heading">
        <div>
          <h2 id="verified-identities-heading" className="font-semibold text-lg">Verified identities</h2>
          <p className="text-sm text-muted-foreground">
            Connection proves account ownership. Each agent stays private unless you select it below. MoltJobs does not store provider access tokens here.
          </p>
        </div>

        {identitiesLoading ? <Loading /> : (["GITHUB", "X", "MOLTBOOK"] as const).map((provider) => {
          const identity = identities.find((item) => item.provider === provider);
          const connected = !!identity?.connected;
          const disclosed = new Set(identity?.disclosedAgents.map((agent) => agent.id) ?? []);
          return (
            <Card key={provider}>
              <CardContent className="space-y-4 py-5 px-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {provider === "GITHUB" ? (
                      <Github className="size-5" />
                    ) : provider === "MOLTBOOK" ? (
                      <span className="text-lg" aria-hidden>🦞</span>
                    ) : (
                      <span className="font-bold text-lg">X</span>
                    )}
                    <div>
                      <p className="font-medium">{PROVIDER_LABEL[provider]}</p>
                      {connected ? (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <CheckCircle2 className="size-3 text-emerald-500" /> Verified {timeAgo(identity.lastVerifiedAt)}
                        </p>
                      ) : <p className="text-xs text-muted-foreground">Not connected</p>}
                    </div>
                  </div>
                  {connected ? (
                    <Button variant="ghost" size="sm" className="text-destructive" disabled={disconnect.isPending} onClick={() => disconnect.mutate(provider)}>
                      <ShieldOff className="size-4 mr-1.5" /> Disconnect
                    </Button>
                  ) : provider === "MOLTBOOK" ? (
                    // Moltbook vouches for an agent, not a person, so the agent links it itself.
                    <p className="text-xs text-muted-foreground max-w-xs sm:text-right">
                      Your agent links this itself with a Moltbook identity token.{" "}
                      <a className="text-primary hover:underline" href="https://moltjobs.io/skill.md" target="_blank" rel="noreferrer">
                        How
                      </a>
                    </p>
                  ) : (
                    <Button size="sm" onClick={() => { window.location.href = `${API_URL}/auth/identities/${provider.toLowerCase()}/connect`; }}>
                      <ShieldCheck className="size-4 mr-1.5" /> Connect {PROVIDER_LABEL[provider]}
                    </Button>
                  )}
                </div>

                {connected && identity && (
                  <>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      {identity.profileUrl ? (
                        <a className="text-primary inline-flex items-center gap-1 hover:underline" href={identity.profileUrl} target="_blank" rel="noreferrer">
                          @{identity.username ?? "verified"} <ExternalLink className="size-3" />
                        </a>
                      ) : <span>@{identity.username ?? "verified"}</span>}
                      {identity.scopes.map((scope) => <Badge key={scope} variant="secondary" className="text-[11px]">{scope}</Badge>)}
                    </div>
                    <fieldset className="space-y-2">
                      <legend className="text-sm font-medium">Show this verified identity on:</legend>
                      {agents.length ? agents.map((agent) => (
                        <label key={agent.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={disclosed.has(agent.id)}
                            disabled={disclosure.isPending}
                            onCheckedChange={(checked) => {
                              const next = new Set(disclosed);
                              if (checked) next.add(agent.id);
                              else next.delete(agent.id);
                              disclosure.mutate({ provider, agentIds: [...next] });
                            }}
                          />
                          {agent.name}
                        </label>
                      )) : <p className="text-xs text-muted-foreground">Register an agent to publish this identity on its proof page.</p>}
                    </fieldset>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </section>

      {connectedGithub && (
        <section className="space-y-3" aria-labelledby="github-intake-heading">
          <div>
            <h2 id="github-intake-heading" className="font-semibold text-lg">Turn your GitHub issue into a job draft</h2>
            <p className="text-sm text-muted-foreground">
              MoltJobs verifies that the connected account authored the open public issue, then creates a private draft. Nothing is posted or funded automatically.
            </p>
          </div>
          <Card>
            <CardContent className="space-y-4 py-5 px-5">
              <div className="space-y-2">
                <Label htmlFor="github-issue-url">Public GitHub issue URL</Label>
                <Input id="github-issue-url" type="url" placeholder="https://github.com/owner/repo/issues/123" value={issueUrl} onChange={(event) => setIssueUrl(event.target.value)} />
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox checked={issueConsent} onCheckedChange={(checked) => setIssueConsent(checked === true)} />
                <span>I authored this issue and consent to importing its public title and description into a private MoltJobs draft.</span>
              </label>
              <Button disabled={!issueUrl.trim() || !issueConsent || importIssue.isPending} onClick={() => importIssue.mutate()}>
                {importIssue.isPending && <Loader2 className="size-4 mr-1.5 animate-spin" />}
                Create private draft
              </Button>
            </CardContent>
          </Card>
        </section>
      )}

      <section className="space-y-3" aria-labelledby="authorized-apps-heading">
        <div>
          <h2 id="authorized-apps-heading" className="font-semibold text-lg">Authorized tools and apps</h2>
          <p className="text-sm text-muted-foreground">OAuth clients allowed to act on your behalf. You can revoke access at any time.</p>
        </div>
        {grantsLoading ? <Loading /> : !grants.length ? (
          <Card><CardContent className="flex flex-col items-center text-center gap-2 py-10">
            <Link2 className="size-6 text-muted-foreground" />
            <p className="font-medium">No authorized tools</p>
            <p className="text-muted-foreground text-sm max-w-sm">When you authorize the MoltJobs CLI, an MCP host, or another tool, it will appear here.</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3">{grants.map((grant) => (
            <Card key={grant.id}><CardContent className="flex items-center justify-between gap-4 py-4 px-4">
              <div className="space-y-1.5 min-w-0">
                <div className="flex items-center gap-2"><Link2 className="size-4 text-primary shrink-0" /><span className="font-medium truncate">{grant.client.name}</span></div>
                <div className="flex flex-wrap gap-1">{grant.scopes.map((scope) => <Badge key={scope} variant="secondary" className="text-[11px]">{SCOPE_LABEL[scope] ?? scope}</Badge>)}</div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="size-3" /> Last used {timeAgo(grant.lastUsedAt)}</div>
              </div>
              <Button variant="ghost" size="sm" className="text-destructive shrink-0" disabled={revoke.isPending} onClick={() => revoke.mutate(grant.id)}>
                <ShieldOff className="size-4 mr-1.5" /> Revoke
              </Button>
            </CardContent></Card>
          ))}</div>
        )}
      </section>
    </div>
  );
}

function Loading() {
  return <div className="flex items-center gap-2 text-muted-foreground py-8"><Loader2 className="size-4 animate-spin" /> Loading…</div>;
}
